import { useEffect, useRef, useState } from "react";
import { Send, Sparkles, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { parseSSEBuffer } from "@/lib/sse";

const QUICK_PROMPTS = [
  "How do I dose BPC-157?",
  "What's the best fat-loss peptide?",
  "Can I stack Ipamorelin with CJC-1295?",
  "Side effects of Semaglutide?",
];

let _msgIdSeq = 0;
const nextId = () => `m${Date.now()}-${++_msgIdSeq}`;

export default function CoachPanel({ context = "" }) {
  const [messages, setMessages] = useState([]); // { id, role, content }
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(() => `guest_${Date.now()}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages]);

  async function streamResponse(res) {
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += dec.decode(value, { stream: true });
      const { events, rest } = parseSSEBuffer(buffer);
      buffer = rest;
      for (const ev of events) {
        if (ev.type === "done") { if (ev.data) setSessionId(ev.data); continue; }
        if (ev.data) {
          setMessages((m) => {
            const copy = [...m];
            const last = copy[copy.length - 1];
            copy[copy.length - 1] = { ...last, content: last.content + ev.data };
            return copy;
          });
        }
      }
    }
  }

  async function send(text) {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    const fullPrompt = context ? `Context: ${context}\n\nUser question: ${message}` : message;
    setMessages((m) => [
      ...m,
      { id: nextId(), role: "user", content: message },
      { id: nextId(), role: "assistant", content: "" },
    ]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: fullPrompt, session_id: sessionId }),
      });
      if (!res.ok) {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { ...copy[copy.length - 1], content: `[error: ${res.status}]` };
          return copy;
        });
        return;
      }
      await streamResponse(res);
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], content: `[network error: ${e.message}]` };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  }

  return (
    <div data-testid="coach-panel" className="bg-[#0A0A0A] border border-[#262626] rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-4 border-b border-[#262626] bg-[#121212]">
        <Sparkles size={16} className="text-[#CCFF00]" />
        <h3 className="font-heading font-bold">AI Peptide Coach</h3>
        <span className="ml-auto text-[10px] font-mono-data text-[#A3A3A3] uppercase tracking-widest">Claude Sonnet 4.5</span>
      </div>

      <div ref={scrollRef} className="h-72 overflow-y-auto p-5 font-mono-data text-sm space-y-4">
        {messages.length === 0 ? (
          <div>
            <p className="text-[#A3A3A3] mb-4"><span className="text-[#CCFF00]">›</span> Ask anything about peptide dosing, stacking, side effects, or cycles.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {QUICK_PROMPTS.map((q) => (
                <button
                  key={q}
                  data-testid={`coach-prompt-${q.slice(0, 12)}`}
                  onClick={() => send(q)}
                  className="text-left p-3 border border-[#262626] rounded hover:border-[#CCFF00]/40 hover:bg-[#121212] text-[#A3A3A3] hover:text-white transition-colors text-xs"
                >
                  <span className="text-[#CCFF00] mr-2">→</span>{q}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m, i) => (
            <div key={m.id} className="leading-relaxed">
              {m.role === "user" ? (
                <div><span className="text-[#CCFF00] font-bold">› you:</span> <span className="text-white">{m.content}</span></div>
              ) : (
                <div>
                  <span className="text-[#00E5FF] font-bold">› coach:</span>{" "}
                  <span className="text-[#E5E5E5] whitespace-pre-wrap">{m.content}</span>
                  {streaming && i === messages.length - 1 && (
                    <span className="inline-block w-2 h-4 bg-[#CCFF00] ml-1 animate-pulse" />
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="border-t border-[#262626] p-3 flex gap-2 bg-[#121212]"
      >
        <span className="text-[#CCFF00] font-mono-data font-bold pl-2 self-center">›</span>
        <input
          data-testid="coach-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type your peptide question..."
          disabled={streaming}
          className="flex-1 bg-transparent outline-none text-white font-mono-data text-sm placeholder:text-[#A3A3A3]"
        />
        <button
          data-testid="coach-send"
          disabled={streaming || !input.trim()}
          className="bg-[#CCFF00] text-black font-bold px-4 py-2 rounded hover:bg-[#B3E600] transition-colors disabled:opacity-40 flex items-center gap-2"
        >
          {streaming ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </button>
      </form>
    </div>
  );
}
